import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import handleGetAcknowledgements from '../../actions/handleGetAcknowledgements';
import {
  selectPolicyTypes,
  selectUserHasSubmittedCheckoutForm,
  selectUserHasCompletedCheckoutForm,
  selectAkHash,
} from '../../selectors/application';
import {
  selectQuoteBindPending,
  selectAcknowledgements,
  selectPartnerAgencyExternalId,
  selectAgentProducerExternalId,
  selectAgentAgencyExternalId,
} from '../../selectors/index';
import { selectQuoteId, selectQuotes, selectStateNoticeText, selectExclusions } from '../../selectors/quote';
import useGetProhibitedOperations from '../../hooks/useGetProhibitedOperations';
import {
  fatalError,
  resetUserHasCompletedCheckoutForm,
  resetUserHasSubmittedCheckoutForm,
  setUserHasCompletedCheckoutForm,
} from '../../actions/application';
import GhostButton from '../Shared/GhostButton';
import SectionDivider from '../Shared/SectionDivider';
import { PolicyType } from '../../types/enums';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import StripeCheckout from './StripeCheckout';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { getResponseContext, reportToSentry, TAGS } from '../../helpers/sentry';
import { usePostBindQuote } from '../../hooks/usePostBindQuote';
import { getPaymentInterval } from '../../selectors/routing';
import { policyPurchased, policyPurchaseFailed, quotePurchasePending } from '../../actions/quote';
import { isProd } from '../../helpers/environment';
import { MobileAndTablet } from '../../helpers/responsive';
import InfoSidebar from '../../Components/Shared/InfoSidebar';
import PaymentDeclinedAlert from './PaymentDeclineAlert';
import PolicyPurchaseErrorAlert from './PolicyPurchaseErrorAlert';
import ScrollableAcknowledgments from './ScrollableAcknowledgments';

export const processingErrorMessage = 'Error processing credit card, please try again';
export const exclusionsAndIneligibleActivitiesDisclaimerText =
  'I confirm that I have reviewed all policy exclusions and ineligible operations relevant to the insured’s operations, including but not limited to those outlined in the policy forms, endorsements, or underwriting guidelines, and that I have ensured the insured understands the scope and limitations of coverage.';

interface CheckoutFormProps {
  quoteId: string;
  quotes: QuoteData[];
  quoteBindPending: boolean;
  stateNoticeText: string;
  acknowledgements: Acknowledgements;
  applicationTypes: PolicyType[];
  userHasSubmittedCheckoutForm: boolean;
  userHasCompletedCheckoutForm: boolean;
  paymentInterval?: string;
  agencyExternalId?: string;
  producerExternalId?: string;
  akHash?: string;
  exclusions: string[];
  handleGetAcknowledgements: () => void;
  resetUserHasCompletedCheckoutForm: () => void;
  resetUserHasSubmittedCheckoutForm: () => void;
  setUserHasCompletedCheckoutForm: () => void;
  fatalError: (error: any) => void;
  policyPurchased: (response: any) => void;
  policyPurchaseFailed: (errors: any) => void;
  quotePurchasePending: () => void;
}

const CheckoutForm = ({
  quoteId,
  quotes,
  quoteBindPending,
  stateNoticeText,
  acknowledgements,
  applicationTypes,
  userHasSubmittedCheckoutForm,
  userHasCompletedCheckoutForm,
  paymentInterval,
  agencyExternalId,
  producerExternalId,
  akHash,
  exclusions,
  handleGetAcknowledgements,
  resetUserHasCompletedCheckoutForm,
  resetUserHasSubmittedCheckoutForm,
  setUserHasCompletedCheckoutForm,
  fatalError,
  policyPurchased,
  policyPurchaseFailed,
  quotePurchasePending,
}: CheckoutFormProps) => {
  const [acceptedTermsAndConditions, setAcceptedTermsAndConditions] = useState(false);
  const [readAcknowledgements, setReadAcknowledgements] = useState(false);
  const [reasonabilityError, setReasonabilityError] = useState('');
  const stripe = useStripe();
  const elements = useElements();
  const { data: prohibitedOperations } = useGetProhibitedOperations(akHash);

  const hasExclusions = exclusions && exclusions.length > 0;
  const hasProhibitedOperations = prohibitedOperations && prohibitedOperations.length > 0;

  const { mutate: postBindRequest, isLoading } = usePostBindQuote({
    onSuccess: (response: any) => {
      if (response.data.isSuccess) {
        policyPurchased(response.data);
      } else {
        policyPurchaseFailed(response.data.errors);
        reportToSentry(response.data.errors, getResponseContext(response));
      }
    },
    onError: (error: any) => {
      reportToSentry(error, undefined, TAGS.STRIPE);
      fatalError(error);
    },
  });

  const readyToSubmit = useCallback(() => {
    if (quoteBindPending || !quoteId) {
      userHasCompletedCheckoutForm && resetUserHasCompletedCheckoutForm();
      return false;
    }

    const hasReadAcknowledgements = applicationTypes.includes(PolicyType.PL) ? readAcknowledgements : true;

    const hasAcceptedAndReadAck = acceptedTermsAndConditions && hasReadAcknowledgements;

    if (hasAcceptedAndReadAck) {
      setUserHasCompletedCheckoutForm();
      return true;
    }

    userHasCompletedCheckoutForm && resetUserHasCompletedCheckoutForm();
    return false;
  }, [
    quoteBindPending,
    quoteId,
    applicationTypes,
    readAcknowledgements,
    acceptedTermsAndConditions,
    userHasCompletedCheckoutForm,
    setUserHasCompletedCheckoutForm,
    resetUserHasCompletedCheckoutForm,
  ]);

  const handleSubmit = useCallback(
    async (e?: any) => {
      e?.preventDefault();

      if (readyToSubmit()) {
        resetUserHasSubmittedCheckoutForm();

        if (isLoading) return;

        const cardElement = elements && elements.getElement('cardNumber');

        if (stripe && cardElement) {
          const { token, error } = await stripe.createToken(cardElement);
          if (error) {
            const hasStripeReasonabilityError = error.type === 'card_error' || error.type === 'validation_error';
            const stripeErrorMsg = error.message;
            if (hasStripeReasonabilityError && stripeErrorMsg) {
              // incomplete card number already has its own inline error messaging under the card inputs
              const hasIncompleteNumberError = error?.code === 'incomplete_number';
              setReasonabilityError(!hasIncompleteNumberError ? stripeErrorMsg : '');
            } else {
              reportToSentry(error, undefined, TAGS.STRIPE);
              fatalError(error);
            }
          } else if (token) {
            setReasonabilityError('');
            quotePurchasePending();
            postBindRequest({
              quoteId,
              tokenId: token.id,
              paymentInterval,
              agencyExternalId,
              producerExternalId,
            });
          }
        } else {
          reportToSentry('Stripe Failed To initialize', undefined, TAGS.STRIPE);
          fatalError('Stripe Failed To initialize');
        }
      }
    },
    [
      elements,
      stripe,
      paymentInterval,
      quoteId,
      agencyExternalId,
      producerExternalId,
      isLoading,
      postBindRequest,
      resetUserHasSubmittedCheckoutForm,
      readyToSubmit,
      fatalError,
      quotePurchasePending,
    ]
  );

  useEffect(() => {
    if (
      applicationTypes.includes(PolicyType.PL) &&
      acknowledgements &&
      !acknowledgements.pl &&
      !quotes[0]?.exclusionSummaryUrl
    ) {
      handleGetAcknowledgements();
    }
  }, [applicationTypes, acknowledgements, handleGetAcknowledgements, quotes]);

  useEffect(() => {
    if (readyToSubmit() && userHasSubmittedCheckoutForm) {
      handleSubmit();
    }
  }, [userHasSubmittedCheckoutForm, handleSubmit, readyToSubmit]);

  const getExclusionSummaryUrl = (): string => {
    if (quotes[0]?.exclusionSummaryUrl) return quotes[0]?.exclusionSummaryUrl;
    if (acknowledgements.pl) return acknowledgements.pl.url;
    return '';
  };

  return (
    <CheckoutFormCont data-testid="checkout_form" onSubmit={handleSubmit}>
      <PolicyPurchaseErrorAlert />
      <MobileAndTablet>
        <InfoSidebar />
      </MobileAndTablet>
      <PaymentCont>
        <SectionTitle>Credit card information</SectionTitle>
        <StripeCheckout reasonabilityError={reasonabilityError} setReasonabilityError={setReasonabilityError} />
      </PaymentCont>
      <PaymentDeclinedAlert />
      <SectionDivider height={24} />
      <ScrollableAcknowledgments
        stateNoticeText={stateNoticeText}
        applicationTypes={applicationTypes}
        acceptedTermsAndConditions={acceptedTermsAndConditions}
        setAcceptedTermsAndConditions={setAcceptedTermsAndConditions}
        readAcknowledgements={readAcknowledgements}
        setReadAcknowledgements={setReadAcknowledgements}
        getExclusionSummaryUrl={getExclusionSummaryUrl}
        hasExclusions={hasExclusions}
        hasProhibitedOperations={hasProhibitedOperations}
      />
      <GhostButton ariaLabel="submit payment" />
    </CheckoutFormCont>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  quoteId: selectQuoteId(state),
  quoteBindPending: selectQuoteBindPending(state),
  stateNoticeText: selectStateNoticeText(state),
  acknowledgements: selectAcknowledgements(state),
  applicationTypes: selectPolicyTypes(state),
  userHasSubmittedCheckoutForm: selectUserHasSubmittedCheckoutForm(state),
  userHasCompletedCheckoutForm: selectUserHasCompletedCheckoutForm(state),
  paymentInterval: getPaymentInterval(state),
  agencyExternalId: selectAgentAgencyExternalId(state) ?? selectPartnerAgencyExternalId(state),
  producerExternalId: selectAgentProducerExternalId(state),
  quotes: selectQuotes(state),
  akHash: selectAkHash(state),
  exclusions: selectExclusions(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch<ReduxState, unknown, AnyAction>) => ({
  resetUserHasCompletedCheckoutForm: () => dispatch(resetUserHasCompletedCheckoutForm()),
  resetUserHasSubmittedCheckoutForm: () => dispatch(resetUserHasSubmittedCheckoutForm()),
  handleGetAcknowledgements: () => dispatch(handleGetAcknowledgements()),
  setUserHasCompletedCheckoutForm: () => dispatch(setUserHasCompletedCheckoutForm()),
  fatalError: (error: any) => dispatch(fatalError(error)),
  policyPurchased: (response: any) => dispatch(policyPurchased(response)),
  policyPurchaseFailed: (errors: any) => dispatch(policyPurchaseFailed(errors)),
  quotePurchasePending: () => dispatch(quotePurchasePending()),
});

const CheckoutFormCont = styled.form`
  position: relative;
  margin-bottom: 32px;
`;

const PaymentCont = styled.div`
  position: relative;
  @media (max-width: 1024px) {
    margin-top: 32px;
  }
  @media print {
    display: none;
  }
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.pageComponents.summary.checkoutForm.sectionTitle.textColor};
  font-size: 24px;
  font-family: ${(props) => props.theme.font.typeface.primary};
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const ConnectedCheckoutForm = connect(mapStateToProps, mapDispatchToProps)(CheckoutForm);

const WrappedCheckoutForm: React.FC = (props) => {
  const elementOptions = {
    fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&display=swap' }],
  };

  // TODO move this to env variable after env refactor
  const publishableKey = isProd()
    ? 'pk_live_51IF1byI0JrvxM1u0ITfcQnT1Xrr6wFh0L4P8rb7zro62HyXxJnUS8Kuo4504s1OdEkO7KiD9epkZ7kzaBjHTkjMo00YfeuGBkE'
    : 'pk_test_51IF1byI0JrvxM1u0rQYl5GKl4jkLGEk6PRVZJrXJ07Rc3tyOlUQPL9aPgusa1fQtRl5NtrTyX7XXuq1jKhevPJNY00vZsNngBX';
  const stripePromise = loadStripe(publishableKey);

  return (
    <Elements options={elementOptions} stripe={stripePromise}>
      <ConnectedCheckoutForm {...props} />
    </Elements>
  );
};

export default WrappedCheckoutForm;
